import { z } from "zod";
import { reconcileStatusSchema } from "./admin-reconcile.js";

/**
 * Shared DTO cho Admin Drift monitor + alerts (US-AE5.F2-0019 · @UC9041 balance-drift-alerts /
 * @UC9042 double-entry-monitor). Một nguồn sự thật cho api (S01/S03) và admin web (S04). Reuse
 * `reconcileStatusSchema` (pass|drift) từ admin-reconcile (DRY — KHÔNG khai báo lại enum status).
 *
 * BẤT BIẾN (invariants):
 * - B1: tiền = BigInt minor units serialize STRING trên wire (không float/number JS). Field money
 *   redact-được (balanceCacheMinor/ledgerSumMinor/driftMinor/totalDriftMinor/deltaMinor) =
 *   `string | null` — vai không xem được tiền (SUPPORT/READ_ONLY) → null (redaction theo vai F1).
 * - `.strict()` — KHÔNG khai báo field lạ (chống vô tình leak tại biên).
 * - G2 (HARD): DTO chỉ mô tả kết quả ĐỌC + cờ điều tra (under_investigation). KHÔNG field mutating
 *   ledger. "Đóng băng nghi vấn" = cờ điều tra (không sửa số ledger) — xem index §6.
 */

/** Max page-size cho bảng drift. Default (20) do backend áp khi vắng. */
export const ADMIN_DRIFT_MAX_LIMIT = 100;

/**
 * Money string trên wire = BigInt minor units. Regex chỉ cho số nguyên (dấu âm hợp lệ vì
 * ledgerSum/cache có thể âm; driftMinor luôn ≥0). Reject float (AC3) & rác. `null` khi redact vai.
 */
const moneyMinorString = z
  .string()
  .regex(/^-?\d+$/, "money phải là số nguyên minor units (không float)");
const moneyMinorNullable = moneyMinorString.nullable();

/**
 * Query GET /admin/reconcile/drift — filter fundId + minDriftMinor + cursor pagination. Chỉ account
 * có drift > 0 (hoặc ≥ minDriftMinor nếu truyền). limit ∈ [1,100] optional; cursor opaque (≤512).
 */
export const driftListQuerySchema = z
  .object({
    fundId: z.string().uuid().optional(),
    /** Ngưỡng drift tối thiểu (minor units string). Vắng → mọi drift > 0. */
    minDriftMinor: z
      .string()
      .regex(/^\d+$/, "minDriftMinor phải là số nguyên không âm (minor units)")
      .optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_DRIFT_MAX_LIMIT).optional(),
  })
  .strict();

export type DriftListQuery = z.infer<typeof driftListQuerySchema>;

/**
 * Một account có drift (UC9041 AC1). Money = STRING minor | NULL (redact theo vai). driftMinor =
 * |balanceCacheMinor − ledgerSumMinor| (tính bằng BigInt phía server, không float). underInvestigation
 * = cờ điều tra (KHÔNG sửa ledger). status luôn 'drift' (list chỉ trả account lệch).
 */
export const driftItemSchema = z
  .object({
    accountId: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable(),
    accountKind: z.string(),
    currency: z.string(),
    /** BigInt minor units as string; null khi redact vai thấp. KHÔNG number. */
    balanceCacheMinor: moneyMinorNullable,
    ledgerSumMinor: moneyMinorNullable,
    driftMinor: moneyMinorNullable,
    status: reconcileStatusSchema,
    /** Cờ "đóng băng nghi vấn" — điều tra, KHÔNG sửa số ledger (AC4). */
    underInvestigation: z.boolean(),
    detectedAt: z.string(),
  })
  .strict();

export type DriftItem = z.infer<typeof driftItemSchema>;

/** Envelope list drift `{ data, pageInfo, driftCount, totalDriftMinor }`. */
export const driftListResponseSchema = z
  .object({
    data: z.array(driftItemSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
    /** Tổng số account lệch (toàn hệ/khớp filter, không theo trang). */
    driftCount: z.number().int().min(0),
    /** Σ|drift| minor units as string; null khi redact vai thấp. */
    totalDriftMinor: moneyMinorNullable,
  })
  .strict();

export type DriftListResponse = z.infer<typeof driftListResponseSchema>;

/**
 * Trạng thái double-entry monitor toàn hệ (UC9042 AC2/AC3). balanced = (Σdebit == Σcredit).
 * triggerAppendOnlyOk = trigger append-only ledger còn hiệu lực (query catalog). Cả 2 false ⇒ báo
 * động regression ngay (feed S03 alert). Money = STRING minor | NULL (redact theo vai).
 */
export const doubleEntryStatusSchema = z
  .object({
    balanced: z.boolean(),
    totalDebitMinor: moneyMinorNullable,
    totalCreditMinor: moneyMinorNullable,
    deltaMinor: moneyMinorNullable,
    /** Trigger append-only (cấm UPDATE/DELETE ledger) còn hiệu lực. false ⇒ regression. */
    triggerAppendOnlyOk: z.boolean(),
    checkedAt: z.string(),
  })
  .strict();

export type DoubleEntryStatus = z.infer<typeof doubleEntryStatusSchema>;

/** Max length note điều tra (chống payload lạm dụng). */
export const DRIFT_INVESTIGATION_NOTE_MAX_LENGTH = 500;

/**
 * Body POST /admin/reconcile/drift/:accountId/investigate (UC9041 AC4) — đánh cờ "đóng băng nghi
 * vấn" cho 1 account. `underInvestigation` bật/tắt cờ; `note` tuỳ chọn. KHÔNG tham số mutating
 * ledger — chỉ cờ điều tra (không sửa số). `.strict()`.
 */
export const markInvestigationRequestSchema = z
  .object({
    underInvestigation: z.boolean(),
    note: z.string().max(DRIFT_INVESTIGATION_NOTE_MAX_LENGTH).optional(),
  })
  .strict();

export type MarkInvestigationRequest = z.infer<typeof markInvestigationRequestSchema>;

/** Response POST investigate — trạng thái cờ sau khi cập nhật. */
export const markInvestigationResponseSchema = z
  .object({
    accountId: z.string(),
    underInvestigation: z.boolean(),
    note: z.string().nullable(),
    markedBy: z.string().nullable(),
    markedAt: z.string(),
  })
  .strict();

export type MarkInvestigationResponse = z.infer<typeof markInvestigationResponseSchema>;
